import { useCallback, useEffect, useRef } from 'react';
import { listHitlTasks } from '../services/hitlTasks.js';
import {
  selectDurableHitlTask,
  buildSyntheticInputRequired,
  isCardStillPending,
} from '../utils/durableHitlCard.js';
import { isHitlContractDismissed } from '../utils/dismissedHitlContracts.js';

/**
 * Milliseconds between background reconciliation polls while a conversation
 * is active. Conservative enough to avoid API hammering; short enough that a
 * card resolved from the inbox disappears without requiring user focus.
 */
const POLL_INTERVAL_MS = 25_000;

/**
 * Durable HITL gate card — survive refresh / conversation switch / missed WS.
 *
 * Replaces the manual clear-on-switch useEffect in BasicChatPanel. On every
 * trigger (conversation change, window focus, slow poll) the hook:
 *
 *  1. Fetches pending tasks (status:'pending', pageSize:50).
 *  2. **Reconciles**: if the current card's contractId is no longer in the
 *     pending list, dismisses it silently (resolved elsewhere — inbox, script,
 *     another device).
 *  3. **Seeds**: if the slot is empty, fills it with the best matching pending
 *     task so the card survives a hard refresh or a missed WS event.
 *
 * Live WebSocket events (handleInputRequired → setHitlRequest) always win
 * because seeding is conditional on the slot being empty.
 *
 * @param {{ apiClient: import('axios').AxiosInstance,
 *   conversationId: string|null|undefined,
 *   hitlRequest: object|null,
 *   setHitlRequest: (v: object|null) => void }} params
 */
export const useDurableHitlCard = ({
  apiClient,
  conversationId,
  hitlRequest,
  setHitlRequest,
}) => {
  // Stable refs so async callbacks never close over stale values.
  const refs = useRef({ hitlRequest, setHitlRequest, conversationId });
  useEffect(() => {
    refs.current = { hitlRequest, setHitlRequest, conversationId };
  });

  const fetchAndSync = useCallback(async () => {
    const { conversationId: cid } = refs.current;
    if (!apiClient || !cid) return;

    let tasks;
    try {
      const { items } = await listHitlTasks(apiClient, {
        status: 'pending',
        pageSize: 50,
      });
      tasks = items;
    } catch {
      return; // network error — leave card state untouched
    }

    const { hitlRequest: req, setHitlRequest: set } = refs.current;

    // Reconcile: dismiss a card that is no longer pending (resolved elsewhere)
    // or one already proven dead this session (zombie: still pending, workflow gone).
    if (req && !isCardStillPending(req, tasks, { isDismissed: isHitlContractDismissed })) {
      set?.(null);
      return;
    }

    // Seed: only fill an empty slot — live WS-delivered cards take precedence.
    if (!req) {
      const best = selectDurableHitlTask(tasks, {
        conversationId: refs.current.conversationId,
        isDismissed: isHitlContractDismissed,
      });
      if (best) {
        set?.(buildSyntheticInputRequired(best));
      }
    }
  }, [apiClient]);

  // Conversation change: clear any card from the old conversation, then fetch.
  const prevConvRef = useRef(conversationId);
  useEffect(() => {
    const prev = prevConvRef.current;
    prevConvRef.current = conversationId;

    if (prev && conversationId && prev !== conversationId) {
      // Always clear the previous conversation's card on switch.
      setHitlRequest?.(null);
    }

    if (conversationId) {
      void fetchAndSync();
    } else {
      // Navigated away from any conversation — clear any lingering card.
      setHitlRequest?.(null);
    }
    // setHitlRequest is stable; fetchAndSync keyed on apiClient. Both are
    // omitted from deps intentionally — only conversationId should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Window focus / visibility restore.
  useEffect(() => {
    const MIN_HIDDEN_MS = 3_000;
    let hiddenAt = null;

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (!refs.current.conversationId) return;
      const start = hiddenAt;
      hiddenAt = null;
      if (start != null && Date.now() - start >= MIN_HIDDEN_MS) {
        void fetchAndSync();
      }
    };

    const onFocus = () => {
      if (!refs.current.conversationId) return;
      void fetchAndSync();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchAndSync]);

  // Slow poll — automatic reconciliation while a conversation is open, so a
  // card resolved from the inbox disappears without requiring user focus.
  useEffect(() => {
    if (!conversationId) return;
    const id = setInterval(() => void fetchAndSync(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [conversationId, fetchAndSync]);
};

export default useDurableHitlCard;
